// Base URL for the API
export const BASE_URL = 'https://api.panchalidesignhub.com/api';

// API endpoints
export const ENDPOINTS = {
  LOGIN: '/accounts/login/',
  REGISTER: '/accounts/register/user/',
  FORGOT_PASSWORD: '/accounts/forgot-password/',
  VERIFY_OTP: '/accounts/verify-password-reset-otp/',
  RESET_PASSWORD: '/accounts/reset-password/',
  CAROUSEL: '/media/carousel/',
  CAROUSEL_DETAIL: (id) => `/media/carousel/${id}/`,
  CAROUSEL_TOGGLE: (id) => `/media/carousel/${id}/toggle_active/`,
  CATEGORIES: '/products/categories/',
  CATEGORY_TOGGLE: (id) => `/products/categories/${id}/toggle_active/`,
  BRANDS: '/products/brands/',
  BRAND_TOGGLE: (id) => `/products/brands/${id}/toggle_active/`,
  TAGS: '/products/tags/',
  TAG_TOGGLE: (id) => `/products/tags/${id}/toggle_active/`,
  TAG_UPDATE: (id) => `/products/tags/${id}/`,
  YOUTUBE_VIDEO: '/media/youtube-video/',
  LOGO: '/media/logo/',
  ABOUT_IMAGES: '/media/about-images/',
  ABOUT_CONTENT: '/media/about-content/',
  ABOUT_CONTENT_UPDATE: (id) => `/media/about-content/${id}/`,
  PRODUCTS: '/products/products/',
  PRODUCT_VARIANTS: (productId) => `/products/products/${productId}/variants/`,
  VARIANT_IMAGES: (productId, variantId) => 
    `/products/products/${productId}/variants/${variantId}/images/`,
  VARIANT_IMAGE: (productId, variantId, imageId) => 
    `/products/products/${productId}/variants/${variantId}/images/${imageId}/`,
  PRODUCT_HIGHLIGHTS: (productId) => `/products/products/${productId}/highlights/`,
  PRODUCT_SPECIFICATIONS: (productId) => `/products/products/${productId}/specifications/`,
  PRODUCT_STYLING_TIPS: (productId) => `/products/products/${productId}/styling-tips/`,
  USERS: '/accounts/users/',
  USER_DETAIL: (id) => `/accounts/users/${id}/`,
  ORDERS: '/orders/orders/',
  ORDER_STATUS_UPDATE: (orderNumber) => `/orders/orders/${orderNumber}/update_status/`,
  REFUND_REQUESTS: '/orders/returns/', // Remove the extra 'api/'
  REFUND_STATUS_UPDATE: (id) => `/orders/returns/${id}/update_status/`,
  PRODUCT_REVIEWS: (productId) => `/products/products/${productId}/reviews/`,
  CUSTOMERS: '/accounts/customers/',
  CUSTOMER_DETAILS: (id) => `/accounts/customers/${id}/`,
  OFFERS: '/products/offers/',
  OFFER_DETAIL: (id) => `/products/offers/${id}/`,
  OFFER_STATUS: (id) => `/products/offers/${id}/toggle_status/`,
  LOGIN_IMAGES: '/media/login-images/',
  LOGIN_IMAGES_DETAIL: (id) => `/media/login-images/${id}/`,
  RETURN_ORDER: '/orders/returns/',
  RETURN_LIMIT: '/orders/return-limit/',
  GET_RETURN_LIMIT: '/orders/return-limit/',  // for GET request
  CREATE_ORDER: '/orders/orders/',
  VERIFY_PAYMENT: (orderNumber) => `/orders/orders/${orderNumber}/test_payment_success/`,
  INDEX_BANNER: '/media/index-banner/',
};

const TOKEN_KEY = 'token';

export const setAuthToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};

export const getAuthToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

export const isAuthenticated = () => {
  const token = getAuthToken();
  return !!token;
};

// API request helper
const apiRequest = async (endpoint, options = {}) => {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Only add Authorization header if token exists AND endpoint is not login or registration
  const token = getAuthToken();
  if (token && ![ENDPOINTS.LOGIN, ENDPOINTS.REGISTER].includes(endpoint)) {
    headers['Authorization'] = `Token ${token}`;
  }

  try {
    const response = await fetch(`${BASE_URL}${endpoint}`, {
      headers,
      ...options,
    });

    // Special cases for endpoints that might return 500 but actually succeed
    if ((endpoint === ENDPOINTS.REGISTER || endpoint === ENDPOINTS.FORGOT_PASSWORD) 
        && response.status === 500) {
      return { success: true };
    }

    // Check content type
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || data.detail || 'API request failed');
      }
      
      return data;
    } else {
      // If not JSON, handle as text
      const text = await response.text();
      if (!response.ok) {
        throw new Error('Server error occurred');
      }
      return text;
    }
  } catch (error) {
    console.error('API Request failed:', error);
    throw error;
  }
};

// API methods
export const api = {
  login: async (email, password) => {
    return apiRequest(ENDPOINTS.LOGIN, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },
  
  forgotPassword: async (email) => {
    try {
      const response = await apiRequest(ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        body: JSON.stringify({ email }),
      });
      return response;
    } catch (error) {
      // If it's not our special case, rethrow the error
      if (!error.message?.includes('Server error occurred')) {
        console.error('Forgot password error:', error);
        throw error;
      }
      // Return success for our special case
      return { success: true };
    }
  },

  verifyOTP: async (email, otp) => {
    return apiRequest(ENDPOINTS.VERIFY_OTP, {
      method: 'POST',
      body: JSON.stringify({ email, otp }),
    });
  },

  resetPassword: async (email, newPassword, confirmPassword) => {
    return apiRequest(ENDPOINTS.RESET_PASSWORD, {
      method: 'POST',
      body: JSON.stringify({
        email,
        new_password: newPassword,
        confirm_password: confirmPassword,
      }),
    });
  },

  register: async (userData) => {
    try {
      const response = await apiRequest(ENDPOINTS.REGISTER, {
        method: 'POST',
        body: JSON.stringify({
          email: userData.email,
          phone_number: userData.phone,
          full_name: userData.name,
          password: userData.password,
          confirm_password: userData.confirmPassword
        }),
      });
      return response;
    } catch (error) {
      // If it's not our special case, rethrow the error
      if (!error.message?.includes('Server error occurred')) {
        console.error('Registration error:', error);
        throw error;
      }
      // Return success for our special case
      return { success: true };
    }
  },

  getCategories: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CATEGORIES}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch categories');
      }

      return response.json();
    } catch (error) {
      console.error('Categories fetch error:', error);
      throw error;
    }
  },

  createCategory: async (formData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CATEGORIES}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type for FormData
        },
        body: formData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create category');
      }

      return response.json();
    } catch (error) {
      console.error('Create category error:', error);
      throw error;
    }
  },

  updateCategory: async (id, formData) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CATEGORIES}${id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update category');
      }

      return response.json();
    } catch (error) {
      console.error('Update category error:', error);
      throw error;
    }
  },

  toggleCategoryActive: async (id) => {
    return apiRequest(ENDPOINTS.CATEGORY_TOGGLE(id), {
      method: 'PATCH',
    });
  },

  // Get all brands
  getBrands: async () => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.BRANDS}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Unauthorized - Please log in again');
        }
        if (response.status === 403) {
          throw new Error('You do not have permission to view brands');
        }
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to fetch brands (${response.status})`);
      }

      const data = await response.json();
      return Array.isArray(data) ? data : (data.results || []);
    } catch (error) {
      console.error('Fetch brands error:', error);
      throw error;
    }
  },

  // Create new brand
  createBrand: async (formData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.BRANDS}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type header when sending FormData
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Failed to create brand');
      }

      return response.json();
    } catch (error) {
      console.error('Create brand error:', error);
      throw error;
    }
  },

  // Update brand
  updateBrand: async (id, formData) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.BRANDS}${id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update brand');
      }

      return response.json();
    } catch (error) {
      console.error('Update brand error:', error);
      throw error;
    }
  },

  // Toggle brand active status
  toggleBrandActive: async (id) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.BRAND_TOGGLE(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to toggle brand status');
      }

      return response.json();
    } catch (error) {
      console.error('Toggle brand status error:', error);
      throw error;
    }
  },

  // Get all tags
  getTags: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.TAGS}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch tags');
      }

      return response.json();
    } catch (error) {
      console.error('Tags fetch error:', error);
      throw error;
    }
  },

  // Create new tag
  createTag: async (tagName) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.TAGS}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: tagName })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to create tag');
      }

      return response.json();
    } catch (error) {
      console.error('Create tag error:', error);
      throw error;
    }
  },

  // Toggle tag active status
  toggleTagActive: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.TAG_TOGGLE(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to toggle tag status');
      }

      return response.json();
    } catch (error) {
      console.error('Toggle tag status error:', error);
      throw error;
    }
  },

  updateTag: async (id, tagData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.TAG_UPDATE(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tagData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to update tag');
      }

      return response.json();
    } catch (error) {
      console.error('Update tag error:', error);
      throw error;
    }
  },

  // Get all carousels
  getCarousels: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CAROUSEL}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch carousels');
      }

      const data = await response.json();
      console.log('Raw API Response:', {
        status: response.status,
        headers: Object.fromEntries(response.headers.entries()),
        data: data
      });

      return data;
    } catch (error) {
      console.error('Carousels fetch error:', error);
      throw error;
    }
  },

  // Update carousel
  updateCarousel: async (id, data) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CAROUSEL_DETAIL(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type when sending FormData
        },
        body: data,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Server response:', errorData);
        throw new Error(
          errorData.detail || 
          errorData.message || 
          (typeof errorData === 'object' ? JSON.stringify(errorData) : 'Failed to update carousel')
        );
      }

      return await response.json();
    } catch (error) {
      console.error('Update carousel error:', error);
      throw error;
    }
  },

  // Delete carousel
  deleteCarousel: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CAROUSEL_DETAIL(id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete carousel');
      }

      return true;
    } catch (error) {
      console.error('Delete carousel error:', error);
      throw error;
    }
  },

  // Toggle carousel active status
  toggleCarouselActive: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CAROUSEL_TOGGLE(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to toggle carousel status');
      }

      return response.json();
    } catch (error) {
      console.error('Toggle carousel error:', error);
      throw error;
    }
  },

  // Create carousel
  createCarousel: async (data, isMultipart = true) => {
    const token = getAuthToken();
    try {
      const headers = {
        'Authorization': `Token ${token}`,
      };

      // Only add Content-Type if not multipart
      if (!isMultipart) {
        headers['Content-Type'] = 'application/json';
      }

      const response = await fetch(`${BASE_URL}${ENDPOINTS.CAROUSEL}`, {
        method: 'POST',
        headers: headers,
        body: isMultipart ? data : JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Failed to create carousel');
      }

      return await response.json();
    } catch (error) {
      console.error('Create carousel error:', error);
      throw error;
    }
  },

  // Get all YouTube videos
  getYoutubeVideos: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.YOUTUBE_VIDEO}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch videos');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : [data];
    } catch (error) {
      console.error('Fetch videos error:', error);
      throw error;
    }
  },

  // Add new YouTube video
  addYoutubeVideo: async (videoData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.YOUTUBE_VIDEO}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: videoData.title,
          video_url: videoData.video_url,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to add video');
      }

      return response.json();
    } catch (error) {
      console.error('Add video error:', error);
      throw error;
    }
  },

  // Get logo
  getLogo: async () => {
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGO}`, {
        method: 'GET',
        // No authorization header for public access
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch logo');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch logo error:', error);
      throw error;
    }
  },

  // Update logo
  updateLogo: async (formData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGO}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type as it's automatically set with FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update logo');
      }

      return response.json();
    } catch (error) {
      console.error('Update logo error:', error);
      throw error;
    }
  },

  // Get about page images
  getAboutImages: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ABOUT_IMAGES}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch about images');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch about images error:', error);
      throw error;
    }
  },

  // Update about page images
  updateAboutImages: async (formData) => {
    const token = getAuthToken();
    const response = await fetch(`${BASE_URL}${ENDPOINTS.ABOUT_IMAGES}`, {
      method: 'POST',
      headers: {
        'Authorization': `Token ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to update about images');
    }

    return response.json();
  },

  // Get about content
  getAboutContent: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ABOUT_CONTENT}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch about content');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch about content error:', error);
      throw error;
    }
  },

  // Create about content
  createAboutContent: async (contentData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ABOUT_CONTENT}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to create about content');
      }

      return response.json();
    } catch (error) {
      console.error('Create about content error:', error);
      throw error;
    }
  },

  // Update about content
  updateAboutContent: async (id, contentData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ABOUT_CONTENT_UPDATE(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contentData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update about content');
      }

      return response.json();
    } catch (error) {
      console.error('Update about content error:', error);
      throw error;
    }
  },

  // Delete about content
  deleteAboutContent: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ABOUT_CONTENT_UPDATE(id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete about content');
      }

      return true;
    } catch (error) {
      console.error('Delete about content error:', error);
      throw error;
    }
  },

  getProduct: async (productId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTS}${productId}/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch product');
      }

      return response.json();
    } catch (error) {
      console.error('Get product error:', error);
      throw error;
    }
  },

  createProduct: async (productData) => {
    const token = getAuthToken();
    try {
      // Log the request data for debugging
      console.log('Creating product with data:', productData);

      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTS}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        // Add timeout and retry logic
        signal: AbortSignal.timeout(30000), // 30 second timeout
        body: JSON.stringify({
          name: productData.name,
          category_id: productData.category_id,
          brand_id: productData.brand_id,
          tag_ids: productData.tag_ids || [],
          price: productData.price,
          sale_price: productData.sale_price,
          description: productData.description || '',
          production_time: productData.production_time || '',
          shipping: productData.shipping || '',
          returns_policy: productData.returns_policy || '',
          is_new_arrival: productData.is_new_arrival || false,
          is_featured: productData.is_featured || false
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create product');
      }

      return response.json();
    } catch (error) {
      console.error('Create product error:', error);
      if (error.name === 'TimeoutError') {
        throw new Error('Request timed out. Please try again.');
      } else if (error.name === 'TypeError' && error.message === 'Failed to fetch') {
        throw new Error('Connection failed. Please check your internet connection and try again.');
      }
      throw error;
    }
  },

  getProductVariants: async (productId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_VARIANTS(productId)}`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch variants');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch variants error:', error);
      throw error;
    }
  },

  addProductVariant: async (productId, variantData) => {
    const token = getAuthToken();
    try {
      // Remove the duplicate 'api' from the URL path
      const response = await fetch(`${BASE_URL}/products/products/${productId}/variants/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          size: variantData.size,
          color: variantData.color,
          color_code: variantData.color_code,
          stock: parseInt(variantData.stock),
          sku: variantData.sku
        }),
      });

      if (!response.ok) {
        // Improved error handling
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message;
        } catch {
          errorMessage = `Server error (${response.status}): ${errorText}`;
        }
        throw new Error(errorMessage || 'Failed to add variant');
      }

      return response.json();
    } catch (error) {
      console.error('Add variant error:', error);
      throw error;
    }
  },

  updateProductVariant: async (productId, variantId, variantData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_VARIANT(productId, variantId)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(variantData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update variant');
      }

      return response.json();
    } catch (error) {
      console.error('Update variant error:', error);
      throw error;
    }
  },

  deleteProductVariant: async (productId, variantId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_VARIANT(productId, variantId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete variant');
      }

      return true;
    } catch (error) {
      console.error('Delete variant error:', error);
      throw error;
    }
  },

  uploadVariantImage: async (productId, variantId, formData) => {
    if (!productId || !variantId) {
      throw new Error('Product ID and Variant ID are required');
    }

    const token = getAuthToken();
    try {
      // Ensure we're using the correct endpoint
      const response = await fetch(`${BASE_URL}${ENDPOINTS.VARIANT_IMAGES(productId, variantId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type as it's automatically set with FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          console.error('Server error response:', errorData); // Add this for debugging
          errorMessage = errorData.detail || errorData.message || errorData.error;
        } catch {
          errorMessage = `Server error (${response.status}): ${errorText}`;
        }
        throw new Error(errorMessage || 'Failed to upload image');
      }

      return response.json();
    } catch (error) {
      console.error('Upload variant image error:', error);
      throw error;
    }
  },

  getVariantImages: async (productId, variantId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.VARIANT_IMAGES(productId, variantId)}`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch variant images');
      }
      return response.json();
    } catch (error) {
      console.error('Fetch variant images error:', error);
      throw error;
    }
  },

  deleteVariantImage: async (productId, variantId, imageId) => {
    if (!productId || !variantId || !imageId) {
      throw new Error('Product ID, Variant ID, and Image ID are required');
    }

    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.VARIANT_IMAGE(productId, variantId, imageId)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || 'Failed to delete image');
      }

      return true;
    } catch (error) {
      console.error('Delete variant image error:', error);
      throw error;
    }
  },

  updateVariantImage: async (productId, variantId, imageId, data) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.VARIANT_IMAGE(productId, variantId, imageId)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      
      if (!response.ok) {
        throw new Error('Failed to update variant image');
      }
      return response.json();
    } catch (error) {
      console.error('Update variant image error:', error);
      throw error;
    }
  },

  createProductHighlight: async (productId, highlightData) => {
    const token = getAuthToken();
    try {
      console.log('Sending highlight data:', { text: highlightData });

      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_HIGHLIGHTS(productId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          text: highlightData,
          is_active: true
        })
      });

      const responseData = await response.json();
      console.log('Server response:', responseData);

      if (!response.ok) {
        throw new Error(
          responseData.detail || 
          responseData.text?.[0] || 
          'Failed to create highlight'
        );
      }

      return responseData;
    } catch (error) {
      console.error('Create highlight error:', error);
      throw error;
    }
  },

  createProductSpecification: async (productId, specData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_SPECIFICATIONS(productId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(specData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create specification');
      }

      return response.json();
    } catch (error) {
      console.error('Create specification error:', error);
      throw error;
    }
  },

  createProductStylingTips: async (productId, stylingData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_STYLING_TIPS(productId)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(stylingData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create styling tips');
      }

      return response.json();
    } catch (error) {
      console.error('Create styling tips error:', error);
      throw error;
    }
  },

  // Get all products
  getProducts: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTS}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch products');
      }

      return response.json();
    } catch (error) {
      console.error('Get products error:', error);
      throw error;
    }
  },

  // Delete a product
  deleteProduct: async (productId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTS}${productId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to delete product');
      }
      
      return true;
    } catch (error) {
      console.error('Delete product error:', error);
      throw error;
    }
  },

  getProductStats: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}/products/stats/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch product stats');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch product stats error:', error);
      throw error;
    }
  },

  exportProducts: async () => {
    const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTS}/export`, {
      method: 'POST',
    });
    if (!response.ok) throw new Error('Failed to export products');
    return await response.blob();
  },

  updateVariant: async (productId, variantId, variantData) => {
    const token = getAuthToken();
    try {
      // Fix the URL structure - remove the duplicate 'api' in the path
      const response = await fetch(`${BASE_URL}/products/products/${productId}/variants/${variantId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          size: variantData.size,
          color: variantData.color,
          color_code: variantData.color_code,
          stock: parseInt(variantData.stock),
          sku: variantData.sku
        }),
      });

      // Improved error handling
      if (!response.ok) {
        let errorMessage = 'Failed to update variant';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorData.message || errorMessage;
        } catch {
          // If response is not JSON, get text content
          const textContent = await response.text();
          errorMessage = `Server error (${response.status}): ${textContent.substring(0, 100)}`;
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error) {
      console.error('Update variant error:', error);
      throw error;
    }
  },

  deleteVariant: async (productId, variantId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}/api/products/products/${productId}/variants/${variantId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete variant');
      }

      return true;
    } catch (error) {
      console.error('Delete variant error:', error);
      throw error;
    }
  },

  getUsers: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.USERS}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch users');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch users error:', error);
      throw error;
    }
  },

  getUserDetails: async (userId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.USER_DETAIL(userId)}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch user details');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch user details error:', error);
      throw error;
    }
  },

  getCustomers: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CUSTOMERS}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch customers');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : (data.results || []);
    } catch (error) {
      console.error('Fetch customers error:', error);
      throw error;
    }
  },

  getAllOrders: async () => {
    try {
      const response = await apiRequest(`${ENDPOINTS.ORDERS}`, {
        method: 'GET',
      });
      return Array.isArray(response) ? response : (response.results || []);
    } catch (error) {
      console.error('Orders fetch error:', error);
      throw new Error(error.message || 'Failed to fetch orders');
    }
  },

  getUserOrderHistory: async (userId) => {
    try {
      // Use the getOrders endpoint and filter by user_id
      const orders = await api.getOrders();
      return orders.filter(order => order.user_id === userId);
    } catch (error) {
      console.error('Error fetching user order history:', error);
      throw new Error(error.message || 'Failed to fetch order history');
    }
  },

  updateOrderStatus: async (orderNumber, formData) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Validate tracking link if present
    const trackingLink = formData.get('tracking_link');
    if (trackingLink) {
      try {
        new URL(trackingLink);
      } catch {
        throw new Error('Invalid tracking link format');
      }
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ORDER_STATUS_UPDATE(orderNumber)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update order status');
      }

      return response.json();
    } catch (error) {
      console.error('Order status update error:', error);
      throw error;
    }
  },

  getRefundRequests: async () => {
    try {
      const response = await apiRequest(ENDPOINTS.REFUND_REQUESTS, {
        method: 'GET',
      });
      
      // Ensure we always return an array
      return Array.isArray(response) ? response : (response.results || []);
    } catch (error) {
      console.error('Refund requests fetch error:', error);
      throw new Error(error.message || 'Failed to fetch refund requests');
    }
  },

  updateRefundStatus: async (refundId, status) => {
    const token = getAuthToken();
    if (!token) {
      throw new Error('No authentication token found');
    }

    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.REFUND_STATUS_UPDATE(refundId)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update refund status');
      }

      return await response.json();
    } catch (error) {
      console.error('Update refund status error:', error);
      if (error.message === 'Unauthorized - Please log in again') {
        // Clear the invalid token
        setAuthToken(null);
      }
      throw error;
    }
  },

  getProductReviews: async () => {
    const token = getAuthToken();
    try {
      const url = `${BASE_URL}/products/reviews/`;
      console.log('Fetching reviews from:', url); // Debug log
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorData.message;
        } catch {
          errorMessage = `Server error (${response.status}): ${errorText}`;
        }
        throw new Error(errorMessage || 'Failed to fetch reviews');
      }

      return response.json();
    } catch (error) {
      console.error('Reviews fetch error:', error);
      throw error;
    }
  },
  getOrders: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.ORDERS}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.detail || `Failed to fetch orders (${response.status})`);
      }

      const data = await response.json();
      console.log('Orders API Response:', data); // Debug log
      return Array.isArray(data) ? data : (data.results || []);
    } catch (error) {
      console.error('Orders fetch error:', error);
      throw new Error(error.message || 'Failed to fetch orders');
    }
  },

  deleteProductHighlight: async (productId, highlightId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_HIGHLIGHTS(productId)}${highlightId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete highlight');
      }
    } catch (error) {
      console.error('Delete highlight error:', error);
      throw error;
    }
  },

  deleteProductSpecification: async (productId, specId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_SPECIFICATIONS(productId)}${specId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete specification');
      }
    } catch (error) {
      console.error('Delete specification error:', error);
      throw error;
    }
  },

  deleteProductStylingTip: async (productId, tipId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_STYLING_TIPS(productId)}${tipId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete styling tip');
      }
    } catch (error) {
      console.error('Delete styling tip error:', error);
      throw error;
    }
  },

  getCustomerDetails: async (customerId) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.CUSTOMER_DETAILS(customerId)}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch customer details');
      }

      return response.json();
    } catch (error) {
      console.error('Fetch customer details error:', error);
      throw error;
    }
  },

  createOffer: async (offerData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.OFFERS}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: offerData.name,
          offer_type: offerData.offerType.toUpperCase(),
          discount_value: offerData.value,
          products: offerData.selectedItems,
          start_date: offerData.startDate,
          end_date: offerData.endDate,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create offer');
      }

      return response.json();
    } catch (error) {
      console.error('Create offer error:', error);
      throw error;
    }
  },

  getOffers: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.OFFERS}`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch offers');
      }

      const data = await response.json();
      return Array.isArray(data) ? data : (data.results || []);
    } catch (error) {
      console.error('Fetch offers error:', error);
      throw error;
    }
  },

  deleteOffer: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.OFFER_DETAIL(id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to delete offer');
      }
    } catch (error) {
      console.error('Delete offer error:', error);
      throw error;
    }
  },

  toggleOfferStatus: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.OFFER_STATUS(id)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to toggle offer status');
      }

      return response.json();
    } catch (error) {
      console.error('Toggle offer status error:', error);
      throw error;
    }
  },

  getOffer: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.OFFERS}${id}/`, {
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch offer');
      }

      return response.json();
    } catch (error) {
      console.error('Get offer error:', error);
      throw error;
    }
  },

  updateOffer: async (id, offerData) => {
    const token = getAuthToken();
    try {
      // Format the data for the API
      const formattedData = {
        name: offerData.name.trim(),
        offer_type: offerData.offerType.toUpperCase(), // Ensure it's uppercase
        discount_value: Number(offerData.value),
        products: offerData.selectedItems,
        start_date: offerData.startDate,
        end_date: offerData.endDate
      };

      // Debug log
      console.log('Sending data to server:', formattedData);

      const response = await fetch(`${BASE_URL}${ENDPOINTS.OFFER_DETAIL(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update offer');
      }

      return response.json();
    } catch (error) {
      console.error('Update offer error:', error);
      throw error;
    }
  },

  getLoginImages: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGIN_IMAGES}`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Get login images error:', error);
      throw error;
    }
  },

  createLoginImage: async (formData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGIN_IMAGES}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data.detail || data.message || Object.values(data)[0]?.[0] || 'Failed to create login image';
        throw new Error(errorMessage);
      }
      return data;
    } catch (error) {
      console.error('Create login image error:', error);
      throw error;
    }
  },

  deleteLoginImage: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGIN_IMAGES_DETAIL(id)}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to delete login image');
      }
    } catch (error) {
      console.error('Delete login image error:', error);
      throw error;
    }
  },

  updateLoginImage: async (id, formData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.LOGIN_IMAGES_DETAIL(id)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (!response.ok) {
        const errorMessage = data.detail || data.message || Object.values(data)[0]?.[0] || 'Failed to update login image';
        throw new Error(errorMessage);
      }
      return data;
    } catch (error) {
      console.error('Update login image error:', error);
      throw error;
    }
  },

  checkReturnEligibility: async () => {
    try {
      const response = await apiRequest(ENDPOINTS.RETURN_LIMIT, {
        method: 'GET',
      });
      return response;
    } catch (error) {
      console.error('Return eligibility check error:', error);
      throw error;
    }
  },

  submitReturnRequest: async (returnData) => {
    try {
      const response = await apiRequest(ENDPOINTS.RETURN_ORDER, {
        method: 'POST',
        body: JSON.stringify({
          order_number: returnData.order_number, // Changed from orderNumber
          reason: returnData.reason,
          description: returnData.description
        }),
      });
      return response;
    } catch (error) {
      console.error('Submit return request error:', error);
      throw error;
    }
  },
  updateProduct: async (id, productData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCTS}${id}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: productData.name,
          category_id: productData.category_id,
          brand_id: productData.brand_id,
          tag_ids: productData.tag_ids,
          price: productData.price,
          sale_price: productData.sale_price,
          description: productData.description,
          production_time: productData.production_time,
          shipping: productData.shipping,
          returns_policy: productData.returns_policy,
          is_new_arrival: productData.is_new_arrival,
          is_featured: productData.is_featured,
          is_active: productData.is_active
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update product');
      }

      return response.json();
    } catch (error) {
      console.error('Update product error:', error);
      throw error;
    }
  },
  updateHighlight: async (productId, highlightId, highlightData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_HIGHLIGHTS(productId)}${highlightId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: highlightData.text,
          is_active: highlightData.is_active
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update highlight');
      }

      return response.json();
    } catch (error) {
      console.error('Update highlight error:', error);
      throw error;
    }
  },
  updateSpecification: async (productId, specId, specData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_SPECIFICATIONS(productId)}${specId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          specification: specData.specification
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update specification');
      }

      return response.json();
    } catch (error) {
      console.error('Update specification error:', error);
      throw error;
    }
  },
  updateStylingTip: async (productId, tipId, tipData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.PRODUCT_STYLING_TIPS(productId)}${tipId}/`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          styling_tips: tipData.styling_tips
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update styling tip');
      }

      return response.json();
    } catch (error) {
      console.error('Update styling tip error:', error);
      throw error;
    }
  },
  getIndexBanner: async () => {
    return apiRequest(ENDPOINTS.INDEX_BANNER, {
      method: 'GET',
    });
  },
  updateIndexBanner: async (formData) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.INDEX_BANNER}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          // Don't set Content-Type as it's automatically set with FormData
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.message || 'Failed to update index banner');
      }

      return response.json();
    } catch (error) {
      console.error('Update index banner error:', error);
      throw error;
    }
  },

  // Get current return limit
  getReturnLimit: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.GET_RETURN_LIMIT}`, {
        method: 'GET',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to fetch return limit');
      }

      const data = await response.json();
      console.log('API Response:', data); // Debug log
      return data;
    } catch (error) {
      console.error('Get return limit error:', error);
      throw error;
    }
  },

  // Update return limit
  updateReturnLimit: async (days) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}${ENDPOINTS.RETURN_LIMIT}`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ days: days }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to update return limit');
      }

      return response.json();
    } catch (error) {
      console.error('Update return limit error:', error);
      throw error;
    }
  },

  // Get all jobs
  getJobs: async () => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}/careers/jobs/`, {
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to fetch jobs');
      return await response.json();
    } catch (error) {
      console.error('Get jobs error:', error);
      throw error;
    }
  },

  // Create job
  createJob: async (data) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}/careers/jobs/`, {
        method: 'POST',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to create job');
      return await response.json();
    } catch (error) {
      console.error('Create job error:', error);
      throw error;
    }
  },

  // Update job
  updateJob: async (id, data) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}/careers/jobs/${id}/`, {
        method: 'PUT',
        headers: {
          'Authorization': `Token ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });
      if (!response.ok) throw new Error('Failed to update job');
      return await response.json();
    } catch (error) {
      console.error('Update job error:', error);
      throw error;
    }
  },

  // Delete job
  deleteJob: async (id) => {
    const token = getAuthToken();
    try {
      const response = await fetch(`${BASE_URL}/careers/jobs/${id}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Token ${token}`,
        },
      });
      if (!response.ok) throw new Error('Failed to delete job');
      return true;
    } catch (error) {
      console.error('Delete job error:', error);
      throw error;
    }
  },
};

export default api;





















































